import { Box, IconButton, Link, Typography } from "@mui/material";
import AttachFile from "@mui/icons-material/AttachFile";
import Close from "@mui/icons-material/Close";
import { DragEvent, useRef, useState } from "react";
import { api } from "../../../api/API";
import { canPreview, FileRef } from "../../../api/Files";
import { Attachment } from "../../../api/Tasks";
import { useImagePreview } from "../../../misc/HtmlImage";
import { useSnackAction } from "../../../misc/SnackAction";
import { panelFontSize, panelGutter, PanelRow, PanelSection } from "./PanelSection";

export function TaskAttachments(props: {
    taskId: number;
    value: Attachment[];
    onChange: (value: Attachment[]) => void;
}) {
    const picker = useRef<HTMLInputElement>(null);

    const add = useSnackAction(async (input: { files: File[] }) => {
        const added: Attachment[] = [];

        // one at a time: each upload is a round trip to the bucket and back
        for (const item of input.files) {
            const stored = await api.files.upload({ file: item });
            await api.tasks.setFile({ task: props.taskId, file: stored.id, archived: false });
            added.push(toAttachment(stored));
        }

        props.onChange(props.value.concat(added));
    }, { success: "Attached" });

    // the file itself is left alone; another task may be holding the same one
    const remove = useSnackAction(async (input: { id: number }) => {
        await api.tasks.setFile({ task: props.taskId, file: input.id, archived: true });

        props.onChange(props.value.filter(file => file.id !== input.id));
    }, { success: "Removed" });

    const busy = add.loading || remove.loading;
    const preview = useImagePreview();

    // dragging is set while files are held over the section
    const [dragging, setDragging] = useState(false);

    // an image shows as a thumbnail, which opens full size; anything else is a row
    const images = props.value.filter(file => file.contentType.startsWith("image/"));
    const others = props.value.filter(file => !file.contentType.startsWith("image/"));

    return (
        <Box
            sx={{ position: "relative" }}
            onDragEnter={e => {
                if (!hasFiles(e)) return;
                e.preventDefault();
                setDragging(true);
            }}
            onDragOver={e => {
                if (!hasFiles(e)) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = "copy";
            }}
            onDragLeave={e => {
                // leaving for a child of the section is still inside it
                if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
                setDragging(false);
            }}
            onDrop={e => {
                if (!hasFiles(e)) return;
                e.preventDefault();
                setDragging(false);

                const files = Array.from(e.dataTransfer.files);
                if (!busy && files.length > 0) add.callback({ files: files });
            }}
        >
            <PanelSection
                label="Attachments"
                noTopBorder
                action={
                    <>
                        <Link
                            component="button"
                            type="button"
                            variant="caption"
                            underline="hover"
                            color="text.secondary"
                            disabled={busy}
                            onClick={() => picker.current?.click()}
                        >
                            Add a file
                        </Link>
                        <input
                            ref={picker}
                            type="file"
                            multiple
                            style={{ display: "none" }}
                            onChange={e => {
                                const files = Array.from(e.target.files || []);

                                // let the same file be picked again after it's been removed
                                e.target.value = "";
                                if (files.length > 0) add.callback({ files: files });
                            }}
                        />
                    </>
                }
            >
                {props.value.length === 0 && (
                    <PanelRow>
                        <Typography variant="caption" color="text.secondary">
                            Keep files and images with this task. Drop them here.
                        </Typography>
                    </PanelRow>
                )}

                {images.length > 0 && (
                    <Box onClick={preview.onClick} sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))",
                        gap: 1,
                        px: 3,
                        py: 1,
                        borderBottom: 1,
                        borderColor: "divider",
                    }}>
                        {images.map(file => (
                            <Box key={file.id} sx={{
                                position: "relative",
                                "&:hover .attachment-remove": { opacity: 1 },
                            }}>
                                <Box component="img" alt={file.name} title={file.name}
                                     src={api.files.downloadUrl({ id: file.id, inline: true })}
                                     sx={{
                                         display: "block", width: "100%", aspectRatio: "1", objectFit: "cover",
                                         borderRadius: 1, border: 1, borderColor: "divider", cursor: "zoom-in",
                                     }} />
                                <IconButton
                                    className="attachment-remove"
                                    size="small"
                                    title="Remove"
                                    disabled={busy}
                                    onClick={e => {
                                        e.stopPropagation();
                                        remove.callback({ id: file.id });
                                    }}
                                    sx={{
                                        position: "absolute", top: 2, right: 2, opacity: 0,
                                        bgcolor: "rgba(255, 255, 255, 0.85)",
                                        "&:hover": { bgcolor: "common.white" },
                                    }}
                                >
                                    <Close fontSize="inherit" />
                                </IconButton>
                            </Box>
                        ))}
                    </Box>
                )}
                {preview.element}

                {others.map(file => {
                    // anything the browser can draw opens in a tab; the rest downloads
                    const preview = canPreview(file);

                    return (
                    <PanelRow key={file.id}>
                        <AttachFile fontSize="inherit" color="action" sx={{ flexShrink: 0 }} />
                        <Link
                            href={api.files.downloadUrl({ id: file.id, inline: preview })}
                            target={preview ? "_blank" : undefined}
                            rel={preview ? "noopener noreferrer" : undefined}
                            variant="body2"
                            underline="hover"
                            color="inherit"
                            title={file.name}
                            sx={{ flex: 1, minWidth: 0, fontSize: panelFontSize, display: "flex", whiteSpace: "nowrap" }}
                        >
                            <FileName name={file.name} />
                        </Link>
                        <Typography variant="caption" color="text.secondary" noWrap>
                            {formatSize(file.size)}
                        </Typography>
                        <IconButton
                            className="panel-row-action"
                            size="small"
                            sx={{ opacity: 0 }}
                            disabled={busy}
                            onClick={() => remove.callback({ id: file.id })}
                        >
                            <Close fontSize="inherit" />
                        </IconButton>
                    </PanelRow>
                    );
                })}
            </PanelSection>

            {dragging && (
                <Box sx={{
                    // the section bleeds out past the panel's padding, so reach out with it
                    position: "absolute", top: 0, bottom: 0, left: -panelGutter, right: -panelGutter,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    bgcolor: "rgba(255, 255, 255, 0.85)",
                    border: 2, borderStyle: "dashed", borderColor: "primary.main",
                    pointerEvents: "none",
                }}>
                    <Typography variant="body2" color="primary">Drop to attach</Typography>
                </Box>
            )}
        </Box>
    );
}

// hasFiles says whether what's being dragged is from outside the page, as opposed
// to a row being moved around inside it
function hasFiles(e: DragEvent) {
    return Array.from(e.dataTransfer.types).includes("Files");
}

// FileName trims a long name in the middle, so the extension is always in view
function FileName(props: { name: string }) {
    const dot = props.name.lastIndexOf(".");
    const base = dot > 0 ? props.name.slice(0, dot) : props.name;
    const ext = dot > 0 ? props.name.slice(dot) : "";

    return (
        <>
            <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
                {base}
            </Box>
            <Box component="span" sx={{ flexShrink: 0 }}>{ext}</Box>
        </>
    );
}

function toAttachment(file: FileRef): Attachment {
    return {
        id: file.id,
        name: file.name,
        contentType: file.contentType,
        size: file.size,
        uploadedAt: file.uploadedAt,
    };
}

const sizeUnits = ["B", "KB", "MB", "GB"];

function formatSize(bytes: number) {
    let value = bytes;
    let unit = 0;

    while (value >= 1024 && unit < sizeUnits.length - 1) {
        value = value / 1024;
        unit++;
    }

    return (unit === 0 ? value : value.toFixed(1)) + " " + sizeUnits[unit];
}
